import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useState } from 'react';
import { benefitsHiringCard } from '@ibk/shared-assets';
import { ContentLayout, StepperCard } from '@ibk/shared-layout';
import { useTranslation } from '@ibk/shared-translation';
import { ProductFamily, VoucherType } from '@ibk/shared-types';
import { getIconName, getTitleKey } from '@ibk/shared-ui';
import { HiringStep, SidebarStatus } from '../../../../types/hiring';
import { ContractSummarySidebar } from '../ContractSummarySidebar';
import { ProductFamilyOption } from '../ProductFamilyOption';
import styles from './HiringProductFamily.module.css';
export function HiringProductFamily({ onComplete, isLoadingPartner, productFamilies, sidebarSteps, productFamiliesForSidebar, stepNumber, shouldShowFamilyOption, }) {
    const { t } = useTranslation();
    const [selectedFamilies, setSelectedFamilies] = useState(new Set(productFamilies));
    const breadcrumbs = [
        { label: t('hiring.breadcrumb.benefits'), href: '/beneficios' },
        { label: t('hiring.breadcrumb.hiring'), isActive: true },
    ];
    const currentStep = {
        id: HiringStep.PRODUCT_FAMILY,
        title: t('hiring.step.product-family'),
        number: stepNumber ?? 1,
        status: SidebarStatus.ACTIVE,
    };
    const handleBack = () => {
        window.history.back();
    };
    const handleFamilyToggle = useCallback((family) => {
        setSelectedFamilies(prev => {
            const newSet = new Set(prev);
            if (newSet.has(family)) {
                newSet.delete(family);
            }
            else {
                newSet.add(family);
            }
            return newSet;
        });
    }, []);
    const handleNext = useCallback(() => {
        if (selectedFamilies.size === 0) {
            return;
        }
        onComplete(Array.from(selectedFamilies));
    }, [onComplete, selectedFamilies]);
    const renderMainCard = () => (_jsx(StepperCard, { currentStep: currentStep, actions: {
            back: {
                label: t('hiring.button.back'),
                onClick: handleBack,
            },
            next: {
                label: t('hiring.button.next'),
                onClick: handleNext,
                disabled: selectedFamilies.size === 0 || isLoadingPartner,
            },
        }, children: _jsxs("div", { className: styles.stepContent, "data-testid": 'viewHiringProductFamilyStepContent', children: [_jsx("div", { className: styles.cardIllustration, "data-testid": 'viewHiringProductFamilyCardIllustration', children: _jsx("img", { src: benefitsHiringCard, alt: t('hiring.card.alt'), className: styles.cardImage, "data-testid": 'imageHiringProductFamilyBenefitsCard' }) }), _jsx("div", { className: styles.explanation, "data-testid": 'viewHiringProductFamilyExplanation', children: _jsx("h3", { className: styles.title, "data-testid": 'textHiringProductFamilyTitle', children: t('hiring.product-family.title') }) }), _jsxs("div", { className: styles.optionsList, "data-testid": 'viewHiringProductFamilyOptionsList', children: [shouldShowFamilyOption(ProductFamily.AUXILIO) && (_jsx(ProductFamilyOption, { isSelected: selectedFamilies.has(ProductFamily.AUXILIO), onToggle: () => handleFamilyToggle(ProductFamily.AUXILIO), titleKey: 'hiring.product-family.option.auxilio.title', descriptionKey: 'hiring.product-family.option.auxilio.description', types: [
                                { icon: getIconName(VoucherType.VA), labelKey: getTitleKey(VoucherType.VA) },
                                { icon: getIconName(VoucherType.VR), labelKey: getTitleKey(VoucherType.VR) },
                                { icon: getIconName(VoucherType.VR_VA), labelKey: getTitleKey(VoucherType.VR_VA) },
                            ], testIdIdentifier: 'Auxilio', t: t })), shouldShowFamilyOption(ProductFamily.PAT) && (_jsx(ProductFamilyOption, { isSelected: selectedFamilies.has(ProductFamily.PAT), onToggle: () => handleFamilyToggle(ProductFamily.PAT), titleKey: 'hiring.product-family.option.pat.title', descriptionKey: 'hiring.product-family.option.pat.description', types: [
                                { icon: getIconName(VoucherType.VA_PAT), labelKey: getTitleKey(VoucherType.VA_PAT) },
                                { icon: getIconName(VoucherType.VR_PAT), labelKey: getTitleKey(VoucherType.VR_PAT) },
                            ], showPatInfo: true, patNumberLabelKey: 'hiring.product-family.option.pat.number-label', testIdIdentifier: 'Pat', t: t }))] })] }) }));
    const renderSummaryCard = () => (_jsx(ContractSummarySidebar, { steps: sidebarSteps, productFamilies: productFamiliesForSidebar }));
    return (_jsx(ContentLayout, { breadcrumbs: breadcrumbs, sidebar: renderSummaryCard(), "data-testid": 'viewHiringProductFamilyContainer', children: renderMainCard() }));
}
